import { Icon } from './icons';
import React, { ReactNode, useId, useEffect, useRef } from 'react';

interface ModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Simple modal primitive: backdrop overlay + centered panel.
 * Closes only through explicit action buttons. Renders nothing when `open` is
 * false. Use the `footer` slot for action buttons so they stay anchored.
 */
export default function Modal({ title, open, onClose, children, footer, className = '' }: ModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`modal ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div
        ref={panelRef}
        className="modal__panel"
        tabIndex={-1}
      >
        <header className="modal__head">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close"><Icon name="close" /></button>
        </header>
        <div className="modal__body">{children}</div>
        {footer && <footer className="modal__foot">{footer}</footer>}
      </div>
    </div>
  );
}
